import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { forwardRef, useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useFieldControl } from './field';
import { Popover, PopoverContent, PopoverTrigger } from './overlay';

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
  /** Secondary text (e.g. parent category). */
  readonly hint?: string;
  readonly icon?: ReactNode;
  /** Extra search terms. */
  readonly keywords?: readonly string[];
}

export interface ComboboxProps {
  readonly value: string | null;
  readonly onChange: (value: string, option: ComboboxOption) => void;
  /** Static options, filtered locally. */
  readonly options?: readonly ComboboxOption[];
  /** Async options for a query (debounced 200 ms). When set, local filtering is disabled. */
  readonly loadOptions?: (query: string) => Promise<readonly ComboboxOption[]>;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyText?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
  /** Controlled open state (e.g. open from a keyboard shortcut). */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** Offers `Create "<typed text>"` when nothing matches the typed name exactly; resolves to the new option (null = failed). */
  readonly onCreate?: (name: string) => Promise<ComboboxOption | null>;
}

/**
 * Ranking for typed search: exact label, then label prefix, then word prefix, then substring, then keyword match
 * (so "Rent" picks Rent before "Rental income"). `value` is `${label} ${id}`.
 */
export const rankOption = (value: string, search: string, keywords?: readonly string[]): number => {
  const q = search.trim().toLowerCase();
  if (!q) return 1;
  const cut = value.lastIndexOf(' ');
  const label = (cut > 0 ? value.slice(0, cut) : value).toLowerCase();
  if (label === q) return 1;
  if (label.startsWith(q)) return 0.9;
  if (label.split(/[\s/&>-]+/).some((w) => w.startsWith(q))) return 0.7;
  if (label.includes(q)) return 0.5;
  return keywords?.some((k) => k.toLowerCase().includes(q)) ? 0.3 : 0;
};

/** Searchable single-select built on cmdk. Type to filter; ↑/↓ and Enter to choose. */
export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      value, onChange, options = [], loadOptions, placeholder = 'Choose…', searchPlaceholder = 'Search…', emptyText = 'No matches.',
      disabled, className, open: openProp, onOpenChange, onCreate, ...aria
    },
    ref,
  ) => {
    const field = useFieldControl();
    const [openState, setOpenState] = useState(false);
    const open = openProp ?? openState;
    const setOpen = (o: boolean) => {
      setOpenState(o);
      onOpenChange?.(o);
    };
    const [query, setQuery] = useState('');
    const [remote, setRemote] = useState<readonly ComboboxOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [known, setKnown] = useState<ComboboxOption | null>(null);

    useEffect(() => {
      if (!loadOptions || !open) return;
      let cancelled = false;
      setLoading(true);
      const t = setTimeout(() => {
        loadOptions(query)
          .then((r) => !cancelled && setRemote(r))
          .catch(() => !cancelled && setRemote([]))
          .finally(() => !cancelled && setLoading(false));
      }, 200);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }, [loadOptions, query, open]);

    const list = loadOptions ? remote : options;
    const typed = query.trim();
    const canCreate = onCreate && typed && !list.some((o) => o.label.toLowerCase() === typed.toLowerCase());
    const pick = (o: ComboboxOption) => {
      setKnown(o);
      onChange(o.value, o);
      setOpen(false);
      setQuery('');
    };
    const selected = [...options, ...remote].find((o) => o.value === value) ?? (known?.value === value ? known : null);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={aria['aria-label']}
            id={field?.id}
            aria-describedby={field?.describedBy}
            aria-invalid={field?.invalid || undefined}
            disabled={disabled}
            className={cn(
              'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border-strong/70 bg-surface px-3 text-left text-sm shadow-sm coarse:h-11',
              'focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:opacity-60',
              selected ? 'text-text' : 'text-text-muted',
              className,
            )}
          >
            <span className="flex min-w-0 items-center gap-2 truncate">
              {selected?.icon}
              {selected?.label ?? placeholder}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-text-muted" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(max(var(--radix-popover-trigger-width),26rem),calc(100vw-2rem))] p-0">
          <Command shouldFilter={!loadOptions} filter={rankOption} loop className="flex flex-col">
            <div className="flex items-center border-b border-border px-3">
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder={searchPlaceholder}
                className="h-11 w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
              />
              {loading ? <Loader2 className="size-4 animate-spin text-text-muted" aria-label="Loading" /> : null}
            </div>
            <Command.List className="max-h-72 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-text-muted">{loading ? 'Searching…' : emptyText}</Command.Empty>
              {list.map((o) => (
                <Command.Item
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  keywords={o.keywords ? [...o.keywords] : undefined}
                  onSelect={() => pick(o)}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-text data-[selected=true]:bg-surface-sunken"
                >
                  <Check className={cn('size-4 text-primary', o.value === value ? 'opacity-100' : 'opacity-0')} aria-hidden />
                  {o.icon}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.hint ? <span className="truncate text-xs text-text-muted">{o.hint}</span> : null}
                </Command.Item>
              ))}
              {canCreate ? (
                <Command.Item
                  forceMount
                  value={`__create__ ${typed}`}
                  onSelect={() => void onCreate(typed).then((o) => o && pick(o))}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-primary data-[selected=true]:bg-surface-sunken"
                >
                  <Plus className="size-4" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">Create “{typed}”</span>
                </Command.Item>
              ) : null}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
Combobox.displayName = 'Combobox';
